import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAdminIncidents, useAdminMaintenances, useAdminAnnouncements } from '@/hooks/useAdminCommunications';
import { formatRelativeTime } from '@/utils/formatters';
import { History, AlertTriangle, Wrench, Megaphone } from 'lucide-react';
import type { Incident, Maintenance, Announcement } from '@/types/admin.types';

interface HistoryItem {
  type: 'incident' | 'maintenance' | 'announcement';
  title: string;
  date: string;
  status?: string;
}

export function CommunicationHistory() {
  const { data: incidentsData } = useAdminIncidents({ limit: 5 });
  const { data: maintenancesData } = useAdminMaintenances({ limit: 5 });
  const { data: announcementsData } = useAdminAnnouncements({ limit: 5 });

  const incidentItems = (incidentsData?.data ?? []) as Incident[];
  const maintenanceItems = (maintenancesData?.data ?? []) as Maintenance[];
  const announcementItems = (announcementsData?.data ?? []) as Announcement[];

  const incidents: HistoryItem[] = incidentItems.map((i) => ({
    type: 'incident' as const,
    title: i.title,
    date: i.createdAt,
    status: i.status,
  }));

  const maintenances: HistoryItem[] = maintenanceItems.map((m) => ({
    type: 'maintenance' as const,
    title: m.title,
    date: m.scheduledStartAt,
    status: m.status,
  }));

  const announcements: HistoryItem[] = announcementItems.map((a) => ({
    type: 'announcement' as const,
    title: a.title,
    date: a.startsAt,
  }));

  const allItems = [...incidents, ...maintenances, ...announcements]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 10);

  const typeConfig = {
    incident: { icon: AlertTriangle, color: 'text-danger-400', label: 'Incident' },
    maintenance: { icon: Wrench, color: 'text-warning-400', label: 'Maintenance' },
    announcement: { icon: Megaphone, color: 'text-primary-400', label: 'Annonce' },
  };

  return (
    <Card>
      <CardHeader title="Historique" />
      <CardContent>
        {allItems.length === 0 ? (
          <EmptyState
            message="Aucun historique"
            description="L'historique des communications sera affiche ici"
            icon={<History className="h-8 w-8" />}
          />
        ) : (
          <div className="space-y-2">
            {allItems.map((item, index) => {
              const config = typeConfig[item.type];
              const Icon = config.icon;
              return (
                <div key={`${item.type}-${index}`} className="flex items-center gap-3 rounded-xl bg-neutral-100 p-3 dark:bg-neutral-800/50">
                  <Icon className={`h-4 w-4 shrink-0 ${config.color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-neutral-700 truncate dark:text-neutral-300">{item.title}</p>
                    <p className="text-xs text-neutral-500">{formatRelativeTime(item.date)}</p>
                  </div>
                  <Badge variant={item.type === 'incident' ? 'danger' : item.type === 'maintenance' ? 'warning' : 'info'}>
                    {config.label}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
